import React, { MouseEvent, useCallback, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import { IPrice } from '@ocx-app/modules/stores/types';

import { Typography } from '@ocx/ui';

import { PriceItemComponent } from '../store-card/price-item.component';
import { BoxItemComponent } from './box-item.component';

const itemsInNotExpandedRow = 4;
const itemsInExpandedRow = 3;

export type PricesGridProps = {
  title?: string;
  prices: IPrice[];
  marginTop?: boolean;
  isExpanded?: boolean;
};

export const PricesGrid: React.FC<PricesGridProps> = ({ title, prices, isExpanded = false, marginTop }) => {
  const canBeExpanded = prices.length > itemsInNotExpandedRow;

  const [isGridExpanded, setIsGridExpanded] = useState(isExpanded);

  const handleExpandGridClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      setIsGridExpanded(true);
    },
    [setIsGridExpanded],
  );

  const gridItems = useMemo(() => {
    return isGridExpanded || !canBeExpanded ? prices : prices.slice(0, itemsInNotExpandedRow - 1);
  }, [isGridExpanded, prices, canBeExpanded]);

  const gridItemSize = useMemo(() => {
    const length = canBeExpanded && !isGridExpanded ? gridItems.length + 1 : gridItems.length;
    const sizeToFillAvailableSpace = 12 / (length || 1);
    return Math.max(12 / (isGridExpanded ? itemsInExpandedRow : itemsInNotExpandedRow), sizeToFillAvailableSpace);
  }, [isGridExpanded, canBeExpanded, gridItems.length]);

  const renderPrices = useCallback(
    ({ id, title, price }: IPrice) => (
      <Grid key={id} item container xs={gridItemSize}>
        <PriceItemComponent title={title} price={price} />
      </Grid>
    ),
    [gridItemSize],
  );

  const renderedMoreItem = useMemo(
    () => (
      <Grid item container xs={gridItemSize}>
        <BoxItemComponent onClick={handleExpandGridClick}>
          <MoreHorizIcon />
        </BoxItemComponent>
      </Grid>
    ),
    [handleExpandGridClick, gridItemSize],
  );

  const spacing = gridItems.length === 2 ? 2 : 1;

  return (
    <>
      {title && (
        <Typography mb={spacing} mt={marginTop ? spacing : 0} variant="body2">
          {title}
        </Typography>
      )}
      <Grid container spacing={spacing}>
        {gridItems.map(renderPrices)}
        {!isGridExpanded && canBeExpanded && renderedMoreItem}
      </Grid>
    </>
  );
};
